import { Injectable } from "@angular/core";

import { AppState } from "../../core/app-state";

import { HttpClient } from "@angular/common/http";
import {
  ScoutPointPhotoService
} from "../../core/services";
import { RestClient } from "../../core/services/rest-client";
import { VolunteerTaskPointRecord, VolunteerTaskPointViewRecord } from "../models/volunteer-task-point-record";
import { VolunteerTaskPointPhotoRecord } from "../models/volunteer-task-point-photo-record";
import { catchError, firstValueFrom, forkJoin, from, map, of } from "rxjs";
import { PhotoContextEvent } from "../../scout/scout-point-photo-links/scout-point-photo-links.component";
import { ScoutPointPhotoDoc } from "../../core/schema/scout-point-photo-schema";
import { defaultBindContextFn } from "../../scout/scout-point/scout-point-photo-browser/scout-point-photo-browser.component";
import { VolunteerCallPhotoViewRecord } from "../models/volunteer-call-photo";
import { ScoutPointInfo } from "../../core/services/scout-point.service";
import { FesRutTestPointPhotoFilter } from "../models";
import { VolunteerTaskService } from "./volunteer-task.service";

export type MobilePhotoContext = {
  iconCssClass: string;
  isVisibleByMobileUser: boolean;
};



const computePhotoSortOrder = (p: ScoutPointPhotoDoc) => {
  let groupOffset = 0;
  switch ( p.group ) {
    case 'measurment':
      break;
    case 'wide-angle':
      groupOffset = 8;
      break;
    case 'point-to-point':
      groupOffset = 2*8;
      break;
    case 'additional':
      groupOffset = 3*8;
      break;
    default:
      groupOffset = 5*8;
  }
  return groupOffset + p.groupOrderIndex;
}

@Injectable({ providedIn: "root" })
export class VolunteerTaskPointService extends RestClient<VolunteerTaskPointRecord> {
  constructor(
    public appState: AppState,
    http: HttpClient,
    private scoutPointPhotoService: ScoutPointPhotoService,
    private volunteerTaskService: VolunteerTaskService
  ) {
    super(http, "volunteerTaskPoint", appState);
  }


  findPhotos = async (taskPointId: number, photoType: 'scout'|'mobile') => {
    console.log(`findPhotos(taskPointId=${taskPointId},photoType=${photoType})`);
    const url = `${this.urlPath}/findPhotos`;
    const params = {taskPointId, photoType };
    console.log(url,params);
    const result: any = await firstValueFrom(this.http.get(url,{params}));
    //console.log('result', result);
    const photos: VolunteerTaskPointPhotoRecord[] = result.data || [];
    return photos;
  }

  findPhotosByCallLogId = async (callLogId: number) => {
    console.log(`findPhotosByCallLogId(${callLogId})`);
    const url = `${this.urlPath}/findPhotosByCallLogId/${callLogId}`;
    console.log(url);
    const result: any = await firstValueFrom(
      this.http.get(`${this.urlPath}/findPhotosByCallLogId/${callLogId}`)
    );
    //console.log('result', result);
    const photos: VolunteerCallPhotoViewRecord[] = result.data || [];
    return photos;
  }

  //assignPhotos = async (taskPointId: number, photos: { photoId: string, sortOrder: number}[]) => {
  //  console.log(`assignPhotos(${taskPointId})`, photos);
  //}

  addMobilePhoto = async (mobilePhoto: VolunteerTaskPointPhotoRecord) => {
    console.log(`addMobilePhoto()`, {mobilePhoto});
    const result: any = await firstValueFrom(
      this.http.post<VolunteerTaskPointPhotoRecord>(`${this.urlPath}/addMobilePhoto`, mobilePhoto, {
        observe: "response",
      })
    );
    const addedMobilePhoto: VolunteerTaskPointPhotoRecord = result.body;
    console.log(JSON.stringify(addedMobilePhoto));
    return addedMobilePhoto;
  };

  removeMobilePhoto = async (mobilePhoto: { taskPointId: number, photoId: string }) => {
    console.log(`removeMobilePhoto(${mobilePhoto})`);
    const result: any = await firstValueFrom(
      this.http.put<VolunteerTaskPointPhotoRecord>(`${this.urlPath}/deleteMobilePhoto`, mobilePhoto, {
        observe: "response",
      })
    );
    const removedMobilePhoto: VolunteerTaskPointPhotoRecord = result.body;
    console.log(JSON.stringify(removedMobilePhoto));
    return removedMobilePhoto;
  };

  addMobilePhotos = async (photos: VolunteerTaskPointPhotoRecord[]) => {
    const addResults = await firstValueFrom(forkJoin(
      photos.map(photo => from(this.addMobilePhoto(photo)).pipe(
        catchError(error => of({ error: error }))
      )
    )));
    const errors = addResults.filter( result => (result as any).error ).map( r=>(r as any).error );
    if ( errors.length ) {
      console.error(errors);
      throw errors[0];
    }
    return addResults;
  };

  removeMobilePhotos = async (photos: { taskPointId: number, photoId: string }[]) => {
    const removeResults = await firstValueFrom(forkJoin(
      photos.map( photo =>
        from(this.removeMobilePhoto(photo)).pipe(
          catchError( error => of({ error: error}) )
        )
    )));
    const errors = removeResults.filter( result => (result as any).error ).map( r=>(r as any).error );
    if ( errors.length ) {
      console.error(errors);
      throw errors[0];
    }
    return removeResults;
  };


  bindMobilePhotoContextForTaskPoint = (rec: VolunteerTaskPointViewRecord) =>
    async (photos: ScoutPointPhotoDoc[], contextByPhotoId: { [id: string]: MobilePhotoContext }) => {
      //console.log(`bindMobilePhotoContextForTaskPoint(${rec.id}).fn`, photos, contextByPhotoId);

      let mobilePhotos: VolunteerTaskPointPhotoRecord[] = [];
      await defaultBindContextFn(photos, contextByPhotoId); // rest call may fail or take some time
      try {
        mobilePhotos = await this.findPhotos(rec.id,'scout');
        //console.log('photos', mobilePhotos)
      } catch (e: any) {
        console.error('could not load mobile photos for taskPoint ', rec.id, e?.message);
      }
      for (const photo of photos) {
        const mobilePhoto = mobilePhotos.find(mp => mp.photoId == photo._id);
        contextByPhotoId[photo._id] = {
          iconCssClass: photoIconCssClass(photo, mobilePhoto),
          isVisibleByMobileUser: !!mobilePhoto
        };
      }
    };

  toggleMobilePhotoForTaskPoint = (rec: VolunteerTaskPointViewRecord) => async (event: PhotoContextEvent<MobilePhotoContext>) => {
    console.log(`toggleMobilePhoto()`, event);
    const { photo, grpDef, contextByPhotoId } = event;
    const ctx = contextByPhotoId[photo._id];
    if (!ctx) {
      console.log('TODO hmmmm.')
      return;
    }
    if (ctx.isVisibleByMobileUser) {
      const removedPhoto = await this.removeMobilePhoto({
        taskPointId: rec.id,
        photoId: photo._id
      });
      ctx.isVisibleByMobileUser = false;
      ctx.iconCssClass = photoIconCssClass(photo)

    } else {
      const addedPhoto = await this.addMobilePhoto({
        taskPointId: rec.id,
        photoId: photo._id,
        fileName: photo.fileName,
        sortOrder: computePhotoSortOrder(photo)
      });
      ctx.isVisibleByMobileUser = true;
      ctx.iconCssClass = photoIconCssClass(photo, addedPhoto)
    }
  }

  deleteById = async ( taskPointId: number ) => {
    console.log(`deleteById(${taskPointId})`);
    if (!taskPointId) {
      throw new Error("Task point id required for delete.");
    }
    const resp = await firstValueFrom(
      this.http.delete<VolunteerTaskPointRecord>(
        this.urlPath + "/" + taskPointId,
        { observe: "response" }
      )
    );
    console.dir(resp);
    const deletedTaskPoint: VolunteerTaskPointRecord = resp.body;
    return deletedTaskPoint;
  };


  async assignVisiblePhotos( args: {
      taskPoints: VolunteerTaskPointRecord[],
      scoutInfoByTaskPtId: {[recId: string]: ScoutPointInfo},
      photoFilters?: FesRutTestPointPhotoFilter[],
      clearExisting?: boolean
      photoType?: 'scout' | 'mobile'
    }
  ){
    let { taskPoints, scoutInfoByTaskPtId, photoFilters, clearExisting, photoType } = args;
    if ( !photoFilters ) {
      photoFilters = (await this.volunteerTaskService.getRemoteConfig()).testPoint.defaults.photoFilters;
    }
    for( const taskPoint of taskPoints ) {
      const taskPtScoutInfo = scoutInfoByTaskPtId[taskPoint.id];
      if ( !taskPtScoutInfo ) {
        console.warn('Could not assign visible photos for volunteer task point (missing scout info)',taskPoint);
        continue;
      }
      const { scoutPoint } = taskPtScoutInfo;
      if ( clearExisting ) {
        const existingPhotos = await this.findPhotos(taskPoint.id,photoType||'scout');
        await this.removeMobilePhotos( existingPhotos.map(p=>({taskPointId: taskPoint.id, photoId: p.photoId})));
      }
      if ( scoutPoint ) {
        const photoDocs = await this.scoutPointPhotoService.rxCollection.find({selector:{
          scoutPointId: scoutPoint.id
        }}).exec();
        const filteredDocs = photoDocs.filter( p => {
          return photoFilters.some(photoFilter=>Object.keys(photoFilter).every( fieldName => {
            const regex = new RegExp(photoFilter[fieldName],'i');
            const matched = regex.test(`${p[fieldName]??''}`);
            console.log(`${fieldName}='${p[fieldName]??''}', regex=${photoFilter[fieldName]}, matched:${matched}`);
            return matched;
          }))
        });
        await this.addMobilePhotos( filteredDocs.map(p=>({
          taskPointId: taskPoint.id,
          photoId: p._id,
          fileName: p.fileName,
          sortOrder: computePhotoSortOrder(p)
        })));
      }
    }
  }

}

export const photoIconCssClass = (photo: ScoutPointPhotoDoc, mobilePhoto: VolunteerTaskPointPhotoRecord = null) =>
  photo?.uploadDate ? uploadedPhotoCss(photo, mobilePhoto) : (photo?.fileName ? 'fa-circle text-warning' : 'fa-times text-info');

export const uploadedPhotoCss = (photo: ScoutPointPhotoDoc, mobilePhoto: VolunteerTaskPointPhotoRecord) => {
  if (mobilePhoto) {
    return 'fa-star text-success';
  } else {
    return 'fa-check text-success';
  }
}

